import { Component, OnDestroy, OnInit } from '@angular/core';
import { environment } from '../../environments/environment';
import { global_variables } from '../../environments/global_variables';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { HttpService } from '../services/http.service';
import { Subscription } from 'rxjs';
import { StorageManagerService } from './../services/storage-manager.service';

/**
 * Landing page for both staff and customers.
 *
 * Layout:
 *  1. "All sensors" — <app-all-sensors-board>, which scopes itself by role
 *     (customers see only their own active sensors; staff get the
 *     all/active/inactive toggle plus a company selector).
 *  2. "Go to" — the destinations this dashboard has always linked to, with
 *     unchanged routes and unchanged role gating.
 *
 * Routing is unchanged: staff land on /dashboard, customers on
 * /:customerId/dashboard (a customer arriving at /dashboard is redirected to
 * their company path, and staff arriving at a company path are sent back).
 */
@Component({
   selector: 'app-dashboard',
   templateUrl: './dashboard.component.html',
   styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
   /** Gates the template until the user record is confirmed. */
   bIsLoading: boolean;
   strUserType: string;
   strUserRole: string;
   public version: string;

   /** Company path segment from the route, e.g. "aqmdasd"; null for staff. */
   customerPath: string;

   userSub: Subscription;
   routeSub: Subscription;

   arrStrUserRoles: string[];
   arrStrUserTypes: string[];

   constructor(
      private _authService: AuthService,
      private _httpService: HttpService,
      private router: Router,
      private _activeRoute: ActivatedRoute,
      private versionService: StorageManagerService
   ) {
      this.bIsLoading = false;
      this.strUserType = '';
      this.strUserRole = '';
      this.arrStrUserRoles = global_variables['userRoles'];
      this.arrStrUserTypes = global_variables['userTypes'];
      this.version = this.versionService.serverAppVersion;
   }

   ngOnInit() {
      this.customerPath = null;

      this.routeSub = this._activeRoute.params.subscribe({
         next: params => {
            this.customerPath = params['customerId'];

            if (this._authService.isCheckUser && this._authService.isUserEmailLoggedIn) {
               this.checkUserAuthentication();
            }
         },
         error: error => console.error('dashboard.component.ts -> this.routeSub: ', error),
      });
   }

   ngOnDestroy() {
      if (this.userSub) {
         this.userSub.unsubscribe();
      }

      if (this.routeSub) {
         this.routeSub.unsubscribe();
      }
   }

   /**
    * Confirm the signed-in user exists in the table matching their type, and
    * keep staff and customers on their respective dashboard URLs.
    */
   checkUserAuthentication() {
      if (this._authService.isUserStaff) {
         this.loadStaffUser();
      } else {
         this.loadCustomerUser();
      }
   }

   /** Staff belong on /dashboard; drop the company path if one is present. */
   private loadStaffUser(): void {
      if (this.customerPath) {
         this.router.navigate(['/dashboard']);
         return;
      }

      this.userSub = this._httpService
         .getAsObject(`${environment.APIS.USERS}/${this.arrStrUserTypes[0]}/${this._authService.currentUserId}`)
         .subscribe({
            next: user => {
               if (user && user['info'] && user['action']) {
                  this.setUserRole(user);
                  this.strUserType = this.arrStrUserTypes[0];
                  this.bIsLoading = true;
               } else {
                  console.log('Log out because there is no user in staff table');
                  this._authService.logout();
               }
            },
            error: error => {
               console.error('dashboard.component.ts -> loadStaffUser(): ', error);
            },
         });
   }

   /** Customers belong on /:customerId/dashboard; redirect if we arrived bare. */
   private loadCustomerUser(): void {
      if (!this.customerPath) {
         const data: Object = this._authService.companyData;
         const path: string = data ? data['path'] : null;

         if (path) {
            this.router.navigate([`/${path}/dashboard`]);
            return;
         }
         // No company path resolved. Rendering here would strand the customer on
         // a bare /dashboard with no tenant context — and AuthguardService
         // .gotoDashboard() would then throw on companyData['path']. Treat it the
         // same as a missing customer record: log out.
         console.log('Log out because no company path could be resolved for this customer');
         this._authService.logout();
         return;
      }

      this.userSub = this._httpService
         .getAsObject(`${environment.APIS.USERS}/${this.arrStrUserTypes[1]}/${this._authService.currentUserId}`)
         .subscribe({
            next: user => {
               if (
                  user &&
                  user['info'] &&
                  user['info']['email'] &&
                  user['action'] &&
                  user['action']['status'] === global_variables['userStatus'][0]
               ) {
                  this.setUserRole(user);
                  this.strUserType = this.arrStrUserTypes[1];
                  this.bIsLoading = true;
               } else {
                  console.log('Log out because there is no user in customer table');
                  this._authService.logout();
               }
            },
            error: error => {
               console.error('dashboard.component.ts -> loadCustomerUser(): ', error);
            },
         });
   }

   /**
    * set user role
    * @param user data
    */
   setUserRole(user: Object) {
      this.strUserRole = user['action']['role'];
   }

   /* ── Links to the destinations this dashboard has always offered ──
      Routes and role gating are unchanged; customer-scoped paths keep their
      company prefix. */

   gotoCustomer() {
      this.router.navigate(['/customer'], { queryParams: { type: 'list' } });
   }

   gotoZone() {
      if (this.customerPath) {
         this.router.navigate([`/${this.customerPath}/zone`], { queryParams: { type: 'list' } });
      } else {
         this.router.navigate(['/zone'], { queryParams: { type: 'list' } });
      }
   }

   gotoSensorList() {
      if (this.customerPath) {
         this.router.navigate([`/${this.customerPath}/sensor_list`]);
      } else {
         this.router.navigate(['sensor_list']);
      }
   }

   /** Staff-only. No `:customId/sensors_dashboard` route exists, so never build one. */
   gotoSensorTypes() {
      if (this.customerPath) {
         this.router.navigate([`/${this.customerPath}/sensor_type_list`]);
      } else {
         this.router.navigate(['sensor_type_list']);
      }
   }

   /** Staff-only. No `:customId/sensor_type` route exists, so never build one. */
   gotoSensorTypeEdit() {
      this.router.navigate(['/sensor_type']);
   }

   gotoAdmin() {
      if (this.customerPath) {
         this.router.navigate([`/${this.customerPath}/admin`]);
      } else {
         this.router.navigate(['/admin']);
      }
   }
}
